import { Link, useParams } from "react-router";
import { BlackoutManager } from "@/design/BlackoutManager";
import { CapacityGrid } from "@/design/CapacityGrid";
import {
  ScheduleOverview,
  type SeatRow,
} from "@/design/ScheduleOverview";
import {
  SeatCalendar,
  ScheduleToolsLocked,
  SeatCalendarUnavailable,
  type SeatDayView,
} from "@/design/SeatCalendar";
import { PageShell } from "@/design/ui";
import { formStateFor as schedulingStateFor } from "@/modules/scheduling/commands/schedulingCopy";
import type {
  Blackout,
  CapacityOverride,
  OpeningHour,
} from "@/modules/scheduling/surfaces";
import { AppLink } from "~/components/AppLink";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { NotFoundScreen } from "~/screens/NotFoundScreen";
import { apiFormAction } from "~/screens/business/SettingsToolScreens";
import { useQuery } from "~/lib/useQuery";

/**
 * The clinic's availability, in two screens (R22 T14).
 *
 * **Since H40 there is no per-practitioner recurring pattern at all.** The
 * clinic's opening hours are the calendar, and a seat is those hours minus two
 * exceptions: when that person is away, and how many fit in an hour (H41).
 * Both screens are shaped to say that rather than to imply everybody keeps
 * their own diary.
 */

// ── The overview (docs/ux/screens/ScheduleScreen.md) ─────────────────────────

interface OverviewPayload {
  gated: boolean;
  hours: { days: number; hours: number } | null;
  rows: SeatRow[] | null;
}

export function ScheduleScreen() {
  const schedule = useQuery<OverviewPayload>("/business/schedule");

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          {schedule.loading && !schedule.data ? (
            <ScreenPending />
          ) : (
            <ScheduleOverview
              gated={schedule.data?.gated ?? false}
              hours={schedule.data?.hours ?? null}
              rows={schedule.data?.rows ?? null}
              configured={schedule.failure !== "not_configured"}
              Link={AppLink}
            />
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}

// ── One seat (docs/ux/screens/PractitionerCalendarScreen.md) ─────────────────

interface SeatPayload {
  gated: boolean;
  practitionerName: string;
  clinicHours: OpeningHour[];
  days: SeatDayView[];
  blackouts: Blackout[];
  overrides: CapacityOverride[];
  today: string;
}

/**
 * **The fortnight re-reads after either editor writes.** A Blackout removes
 * open hours and a capacity change alters how full each one reads, so leaving
 * the calendar above them stale would show a seat that is available at a time
 * it just stopped being.
 */
export function PractitionerCalendarScreen() {
  const { practitionerId = "" } = useParams();
  const seat = useQuery<SeatPayload>(
    practitionerId
      ? `/business/practitioners/${practitionerId}/schedule`
      : null,
  );
  const reload = () => seat.reload();

  if (seat.failure === "not_found") return <NotFoundScreen />;

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          <Link
            to="/business/schedule"
            className="mb-4 inline-block text-sm font-medium text-accent"
          >
            ← Back to schedule
          </Link>

          {seat.loading && !seat.data ? (
            <ScreenPending />
          ) : seat.failure || !seat.data ? (
            <SeatCalendarUnavailable
              configured={seat.failure !== "not_configured"}
            />
          ) : seat.data.gated ? (
            <ScheduleToolsLocked Link={AppLink} />
          ) : (
            <>
              <SeatCalendar
                practitionerName={seat.data.practitionerName}
                hasClinicHours={seat.data.clinicHours.length > 0}
                days={seat.data.days}
                Link={AppLink}
              />
              <BlackoutManager
                practitionerId={practitionerId}
                blackouts={seat.data.blackouts}
                today={seat.data.today}
                add={apiFormAction({
                  method: "POST",
                  path: () =>
                    `/business/practitioners/${practitionerId}/blackouts`,
                  render: schedulingStateFor,
                  onSuccess: reload,
                })}
                remove={apiFormAction({
                  method: "DELETE",
                  path: (form) =>
                    `/business/practitioners/${practitionerId}/blackouts/${String(
                      form.get("blackoutId") ?? "",
                    )}`,
                  render: schedulingStateFor,
                  onSuccess: reload,
                })}
              />
              <CapacityGrid
                practitionerId={practitionerId}
                clinicHours={seat.data.clinicHours}
                overrides={seat.data.overrides}
                save={apiFormAction({
                  method: "PUT",
                  path: () =>
                    `/business/practitioners/${practitionerId}/capacity`,
                  render: schedulingStateFor,
                  onSuccess: reload,
                })}
              />
            </>
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}
